/**
 * CR-6444: sectioned layout for the Quality → NCR filter panel.
 *
 * Regroups the existing filters into the five sections from the supplied
 * design — Date Range · Company · Supplier · Classification · NCR Details —
 * without changing any control type or filter behaviour.
 *
 * Two things are fixed on the way through, both visible to users today:
 *  - Commodity Group / Commodity Type and the Strategic-suppliers tickbox were
 *    each rendered TWICE, bound to the same state, so the panel showed
 *    duplicate controls that moved together. Each now appears once.
 *  - The later rows were styled with hardcoded `text-gray-700` / `bg-white`,
 *    which ignores the theme and reads wrong in dark mode. All controls now use
 *    the shared filter styling.
 *
 * Six filters the design omits are deliberately kept — Search, UK/EU, Claim
 * Status, Action Status and the two Collection Date fields. Removing working
 * filters would breach the ticket's own "no regressions" criterion, so they are
 * placed in the section they belong to and raised on the ticket instead.
 */
import { Search } from 'lucide-react';
import { Input } from '../ui/input';
import SearchableSelect from '../ui/SearchableSelect';
import {
  FilterSection, FilterGrid, FilterField, filterControlClass,
} from '../filters/FilterSection';
import type { NCRFiltersState } from './NCRFiltersBar';

interface IdName { id: number; name: string; code?: string | null }
interface SupplierProfileOption { company_id?: number | null; company?: number | null; company_name: string }
interface LabelledOption { value: string; label: string }

export interface NCRFilterSectionsProps {
  values: NCRFiltersState;
  onChange: (next: Partial<NCRFiltersState>) => void;
  quickRanges: ReadonlyArray<{ id: string; label: string }>;
  activeQuickRange: string | null;
  onQuickRange: (id: string) => void;
  sites: IdName[];
  warehouses: IdName[];
  supplierGroups: IdName[];
  filteredSuppliers: SupplierProfileOption[];
  commodityGroups: IdName[];
  filteredCommodityTypes: IdName[];
  regionOptions: string[];
  liabilityOptions: ReadonlyArray<LabelledOption>;
  claimStatusOptions: ReadonlyArray<LabelledOption>;
  actionStatusOptions: ReadonlyArray<LabelledOption>;
  /**
   * CR-6451: false on the supplier portal. A supplier filtering NCRs by
   * "strategic suppliers only" is filtering on a property of themselves —
   * the same reasoning that removed supplier-country from supplier-portal
   * pages in CR-4887 — and the supplier design omits it.
   */
  showStrategicFilter?: boolean;
}

export function NCRFilterSections({
  values,
  onChange,
  quickRanges,
  activeQuickRange,
  onQuickRange,
  sites,
  warehouses,
  supplierGroups,
  filteredSuppliers,
  commodityGroups,
  filteredCommodityTypes,
  regionOptions,
  liabilityOptions,
  claimStatusOptions,
  actionStatusOptions,
  showStrategicFilter = true,
}: NCRFilterSectionsProps) {
  /** Native <select> bound to one filter key — unchanged element and handler. */
  const select = (
    label: string,
    key: keyof NCRFiltersState,
    placeholder: string,
    options: ReadonlyArray<LabelledOption>,
    extra?: Partial<NCRFiltersState>,
  ) => (
    <FilterField label={label}>
      <select
        value={String(values[key] ?? '')}
        onChange={(e) => onChange({ [key]: e.target.value, ...(extra || {}) } as Partial<NCRFiltersState>)}
        className={filterControlClass}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </FilterField>
  );

  const dateField = (label: string, key: 'dateFrom' | 'dateTo' | 'collectionDateFrom' | 'collectionDateTo') => (
    <FilterField label={label}>
      <input
        type="date"
        value={values[key]}
        onChange={(e) => onChange({ [key]: e.target.value } as Partial<NCRFiltersState>)}
        className={filterControlClass}
      />
    </FilterField>
  );

  return (
    <>
      <FilterSection title="Date Range" bare>
        <div className="flex flex-wrap gap-2 mb-4">
          {quickRanges.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => onQuickRange(id)}
              className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors ${
                activeQuickRange === id
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <FilterGrid>
          {dateField('Date From', 'dateFrom')}
          {dateField('Date To', 'dateTo')}
        </FilterGrid>
      </FilterSection>

      <FilterSection title="Company" bare>
        <FilterGrid>
          <FilterField label="Site">
            <SearchableSelect
              options={sites.map((s) => ({ value: String(s.id), label: s.name }))}
              value={values.siteFilter}
              onChange={(val) => onChange({ siteFilter: val })}
              placeholder="All Sites"
            />
          </FilterField>
          {select('Warehouse Location', 'warehouseFilter', 'All Warehouses',
            warehouses.map((w) => ({
              value: String(w.id),
              label: w.code ? `${w.code} — ${w.name}` : w.name,
            })))}
        </FilterGrid>
      </FilterSection>

      {/* Supplier — Supplier Group still clears the Supplier Site selection so
          the two can never describe an impossible combination. */}
      <FilterSection title="Supplier" bare>
        <FilterGrid>
          <FilterField label="Supplier Group">
            <SearchableSelect
              options={supplierGroups.map((g) => ({ value: String(g.id), label: g.name }))}
              value={values.supplierGroupFilter}
              onChange={(val) => onChange({ supplierGroupFilter: val, supplierFilter: '' })}
              placeholder="All Supplier Groups"
            />
          </FilterField>
          <FilterField label="Supplier Site">
            <SearchableSelect
              // Use company_id — the minimal profiles endpoint returns
              // company_id, NOT company. String(s.company) yielded 'undefined',
              // sent as supplier=undefined → 400. Guard out profiles with
              // neither.
              options={filteredSuppliers
                .filter((s) => (s.company_id ?? s.company) != null)
                .map((s) => ({ value: String(s.company_id ?? s.company), label: s.company_name }))}
              value={values.supplierFilter}
              onChange={(val) => onChange({ supplierFilter: val })}
              placeholder="All Supplier Sites"
            />
          </FilterField>
        </FilterGrid>
        {showStrategicFilter && (
        <label className="flex items-center gap-2 cursor-pointer mt-4 w-fit">
          <input
            type="checkbox"
            checked={values.strategicFilter}
            onChange={(e) => onChange({ strategicFilter: e.target.checked })}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-2"
          />
          <span className="text-sm font-medium text-foreground">Strategic suppliers only</span>
        </label>
        )}
      </FilterSection>

      <FilterSection title="Classification" bare>
        <FilterGrid>
          {/* CR-4802: hidden together when the tenant hasn't configured any
              commodity groups in Admin Setup — commodityGroups here is the
              full tenant list NCRFiltersBar fetches for the dropdown, not
              narrowed by selection, so it's a valid "configured at all"
              signal (mirrors the Dashboard ClassificationSection gate). */}
          {commodityGroups.length > 0 && select('Commodity Group', 'commodityGroupFilter', 'All Commodity Groups',
            commodityGroups.map((g) => ({ value: String(g.id), label: g.name })),
            // Changing the group invalidates the selected type.
            { commodityTypeFilter: '' })}
          {commodityGroups.length > 0 && select('Commodity Type', 'commodityTypeFilter', 'All Commodity Types',
            filteredCommodityTypes.map((t) => ({ value: String(t.id), label: t.name })))}
          {/* Kept although absent from the design — an existing filter. */}
          {select('UK/EU', 'regionFilter', 'All Regions',
            regionOptions.map((c) => ({ value: c, label: c })))}
        </FilterGrid>
      </FilterSection>

      <FilterSection title="NCR Details" bare>
        <FilterGrid>
          <FilterField label="Search">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search NCRs..."
                value={values.searchTerm}
                onChange={(e) => onChange({ searchTerm: e.target.value })}
                className="pl-9 bg-background"
              />
            </div>
          </FilterField>
          {select('Status', 'statusFilter', 'All Statuses', [
            { value: 'draft', label: 'Draft' },
            { value: 'created', label: 'Created' },
            { value: 'ca_submitted', label: 'CA Submitted' },
            { value: 'car_submitted', label: 'CAR Submitted' },
            { value: 'closed', label: 'Closed' },
            { value: 'deleted', label: 'Deleted' },
          ])}
          <FilterField label="Part Number">
            <Input
              placeholder="Part number..."
              value={values.partNumberFilter}
              onChange={(e) => onChange({ partNumberFilter: e.target.value })}
              className="bg-background"
            />
          </FilterField>
          {select('Liability', 'liabilityFilter', 'All Liabilities', liabilityOptions)}
          {select('Claim Status', 'claimStatusFilter', 'All Claim Statuses', claimStatusOptions)}
          {select('Action Status', 'actionStatusFilter', 'All Action Statuses', actionStatusOptions)}
          {dateField('Collection Date From', 'collectionDateFrom')}
          {dateField('Collection Date To', 'collectionDateTo')}
        </FilterGrid>
      </FilterSection>
    </>
  );
}
